import { useTranslation } from "react-i18next";
import { ScrollReveal } from "@/components/ScrollReveal";
import { useCountUp } from "@/hooks/useCountUp";
import { ArrowRight, Check } from "lucide-react";

const statMeta = [
  { end: 40, suffix: "+", code: "SHP" },
  { end: 100, suffix: "%", code: "CUS" },
  { end: 99.9, suffix: "%", decimals: 0, code: "UPT" },
  { end: 24, prefix: "< ", suffix: "h", code: "SLA" },
];

const R = 38;
const CIRC = 2 * Math.PI * R;

const RadialGauge = ({
  end,
  suffix = "",
  prefix = "",
  decimals = 0,
  label,
  code,
  index,
}: {
  end: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
  label: string;
  code: string;
  index: number;
}) => {
  const { ref, count } = useCountUp({ end, suffix, prefix, decimals, duration: 2400 });
  // Ring tracks the count-up progress and always lands on a full, "achieved" circle.
  const progress = end ? Math.min(Math.max(count / end, 0), 1) : 1;
  const offset = CIRC * (1 - progress);
  const display = `${prefix}${count.toFixed(decimals)}${suffix}`;
  const gradId = `gauge-grad-${index}`;

  return (
    <div className="relative flex flex-col items-center justify-center py-5 group">
      {/* faint code tag */}
      <span className="absolute top-2 right-3 font-mono text-[8px] tracking-wider text-accent/30">
        0{index + 1}/{code}
      </span>

      <div className="relative w-24 h-24 md:w-28 md:h-28">
        {/* rotating dashed targeting ring */}
        <div className="absolute inset-[-3px] rounded-full border border-dashed border-accent/15 hud-spin-slow" />
        <div className="absolute inset-1 rounded-full border border-accent/10 hud-spin-rev" />

        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          <defs>
            <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="hsl(var(--primary))" />
              <stop offset="100%" stopColor="hsl(var(--accent))" />
            </linearGradient>
          </defs>
          <circle cx="50" cy="50" r={R} fill="none" stroke="hsl(var(--border))" strokeWidth="5" />
          <circle
            cx="50"
            cy="50"
            r={R}
            fill="none"
            stroke={`url(#${gradId})`}
            strokeWidth="5"
            strokeLinecap="round"
            strokeDasharray={CIRC}
            strokeDashoffset={offset}
            style={{
              filter: "drop-shadow(0 0 5px hsl(var(--accent) / 0.6))",
              transition: "stroke-dashoffset 90ms linear",
            }}
          />
        </svg>

        <div className="absolute inset-0 flex items-center justify-center">
          <p ref={ref} className="text-xl md:text-2xl font-bold text-foreground tabular-nums">
            {display}
          </p>
        </div>
      </div>

      <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground text-center px-2">
        {label}
      </p>
    </div>
  );
};

const AboutSection = () => {
  const { t } = useTranslation();
  const points = t("about.points", { returnObjects: true }) as string[];
  const statLabels = t("about.stats", { returnObjects: true }) as string[];
  const stats = statMeta.map((s, i) => ({ ...s, label: statLabels[i] ?? "" }));
  return (
    <section id="about" className="relative section-y bg-secondary/40 overflow-hidden">
      <div className="absolute inset-0 tech-grid opacity-[0.1] pointer-events-none" />
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="aurora-blob aurora-2 w-[45%] h-[45%] top-[10%] right-[-8%]" />
      </div>

      <div className="container relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* LEFT — narrative + boot sequence */}
          <div>
            <ScrollReveal direction="left">
              <h2 className="text-[1.55rem] sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight mb-6">
                {t("about.titlePrefix")} <span className="gradient-text">{t("about.titleHighlight")}</span> {t("about.titleSuffix")}
              </h2>
              <p className="text-[15px] sm:text-lg text-muted-foreground leading-relaxed mb-8 max-w-lg">
                {t("about.body")}
              </p>
            </ScrollReveal>

            <ul className="space-y-3 mb-10">
              {points.map((point, i) => (
                <ScrollReveal
                  as="li"
                  key={point}
                  delay={300 + i * 120}
                  direction="left"
                  distance={20}
                  className="flex items-center gap-3 group"
                >
                  <span className="flex items-center justify-center w-5 h-5 rounded-full border border-accent/40 bg-accent/10 shrink-0 transition-[background-color,border-color] duration-200 group-hover:bg-accent/20 group-hover:border-accent/70">
                    <Check size={12} className="text-accent" strokeWidth={3} />
                  </span>
                  <span className="text-foreground">{point}</span>
                </ScrollReveal>
              ))}
            </ul>

            <ScrollReveal delay={800} direction="up" distance={20}>
              <span className="hover-glow-accent block w-full sm:inline-block sm:w-auto">
                <a
                  href="/contact"
                  className="inline-flex w-full sm:w-auto justify-center items-center gap-2 px-7 py-3.5 btn-clip bg-primary text-primary-foreground btn-label-contrast font-medium transition-transform duration-200 active:scale-[0.97] group"

                >
                  {t("about.cta")}
                  <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              </span>
            </ScrollReveal>
          </div>

          {/* RIGHT — live metrics HUD */}
          <ScrollReveal direction="right" delay={200}>
            <div className="relative rounded-2xl border border-border bg-card/70 backdrop-blur-sm overflow-hidden shadow-2xl">
              {/* tech dots wash */}
              <div className="absolute inset-0 tech-dots opacity-[0.15] pointer-events-none" />

              {/* corner brackets */}
              <span className="absolute top-2 left-2 w-4 h-4 border-t border-l border-accent/40" />
              <span className="absolute top-2 right-2 w-4 h-4 border-t border-r border-accent/40" />
              <span className="absolute bottom-2 left-2 w-4 h-4 border-b border-l border-accent/40" />
              <span className="absolute bottom-2 right-2 w-4 h-4 border-b border-r border-accent/40" />

              {/* sweeping scanline */}
              <span className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/60 to-transparent hud-scan pointer-events-none" />

              {/* header */}
              <div className="relative flex items-center justify-between px-5 py-3 border-b border-border bg-background/40">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-success hud-blink" />
                  <span className="font-mono text-[11px] tracking-wide text-muted-foreground">
                    system://devcore.metrics
                  </span>
                </div>
                <span className="flex items-center gap-1.5 font-mono text-[10px] tracking-[0.18em] text-accent">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                  LIVE
                </span>
              </div>

              {/* gauges */}
              <div className="relative grid grid-cols-2 divide-x divide-y divide-border/60">
                {stats.map((s, i) => (
                  <RadialGauge key={s.label} {...s} index={i} />
                ))}
              </div>

              {/* footer — animated activity bars */}
              <div className="relative flex items-center justify-between px-5 py-3 border-t border-border bg-background/40">
                <span className="font-mono text-[10px] tracking-wide text-muted-foreground">
                  uptime_monitor
                </span>
                <div className="flex items-end gap-1 h-4">
                  {[0, 1, 2, 3, 4, 5, 6, 7].map((b) => (
                    <span
                      key={b}
                      className="w-1 rounded-sm bg-accent/70 hud-bar"
                      style={{ height: "100%", animationDelay: `${b * 0.12}s` }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
